import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";

import portrait from "@/assets/lizy-portrait.jpg";
import { home, pillars, site } from "@/data/content";
import { Reveal, MaskedLines } from "@/components/site/Reveal";
import EditorialMarquee from "@/components/site/Marquee";
import BookCTA from "@/components/site/BookCTA";

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const portraitY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const portraitScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section
        ref={heroRef}
        className="relative min-h-[100svh] pt-28 md:pt-32 pb-16 overflow-hidden"
      >
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-center">
          {/* Text */}
          <div className="lg:col-span-7 order-2 lg:order-1">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="overline mb-6"
              data-testid="hero-overline"
            >
              {site.role} · {site.specialty}
            </motion.p>

            <h1 className="font-display text-[13vw] sm:text-7xl lg:text-[6.2rem] leading-[0.9] tracking-tight">
              <MaskedLines lines={home.heroLines} delay={0.15} />
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.6 }}
              className="mt-8 text-lg md:text-xl text-[#5c5c5c] max-w-xl leading-relaxed"
            >
              {home.heroSub}
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.75 }}
              className="mt-6 text-base text-[#1a1a1a]/80 max-w-lg leading-relaxed"
            >
              {home.heroDescription}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.9 }}
              className="mt-10"
            >
              <Link
                to="/agenda"
                data-testid="hero-cta-agenda"
                className="group inline-flex items-center gap-3 rounded-full bg-[#9C5A4B] text-[#f9f8f6] px-7 py-4 text-sm tracking-wide hover:bg-[#8A4A3C] transition-colors duration-300"
              >
                Agendar una primera entrevista
                <ArrowRight
                  size={18}
                  className="transition-transform duration-300 group-hover:translate-x-1"
                />
              </Link>
            </motion.div>
          </div>

          {/* Portrait */}
          <div className="lg:col-span-5 order-1 lg:order-2">
            <motion.div
              initial={{ opacity: 0, scale: 1.05 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
              className="relative mx-auto max-w-[360px] lg:max-w-none"
            >
              <div className="absolute -inset-3 border border-[#9C5A4B]/40 translate-x-4 translate-y-4 pointer-events-none" />
              <div className="relative overflow-hidden aspect-[4/5]">
                <motion.img
                  src={portrait}
                  alt="Lizy Rodríguez, Psicóloga Clínica"
                  style={{ y: portraitY, scale: portraitScale }}
                  className="portrait-frame w-full h-full object-cover"
                  data-testid="hero-portrait"
                />
              </div>
            </motion.div>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.4 }}
          className="hidden lg:flex absolute bottom-8 left-1/2 -translate-x-1/2 items-center gap-2 text-xs tracking-widest uppercase text-[#5c5c5c]"
        >
          <ArrowDown size={14} /> Desliza
        </motion.div>
      </section>

      {/* QUESTION */}
      <section className="bg-[#efece5]" data-testid="home-question">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-24 md:py-36">
          <Reveal>
            <p className="font-display italic text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.15] tracking-tight max-w-4xl">
              {home.question}
            </p>
          </Reveal>
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-24 md:py-36">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12">
          <div className="md:col-span-3">
            <Reveal>
              <span className="font-display text-7xl md:text-8xl text-[#9C5A4B]/30 leading-none">
                01
              </span>
            </Reveal>
          </div>
          <div className="md:col-span-8 md:col-start-5">
            <Reveal delay={0.1}>
              <p className="text-xl md:text-2xl leading-relaxed text-[#1a1a1a]/90 font-light">
                {home.manifesto}
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="bg-[#2A2C29] text-[#f9f8f6]" data-testid="home-pillars">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-24 md:py-32">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {pillars.map((p, i) => (
              <Reveal
                key={p.number}
                delay={i * 0.12}
                className="bg-[#2A2C29] p-8 md:p-12 min-h-[280px] flex flex-col justify-between group hover:bg-[#31332f] transition-colors duration-500"
              >
                <span className="overline text-white/40">{p.number}</span>
                <div>
                  <h3 className="font-display italic text-4xl md:text-5xl mb-4 text-[#f9f8f6] group-hover:text-[#c98a78] transition-colors duration-500">
                    {p.title}
                  </h3>
                  <p className="text-white/60 leading-relaxed max-w-xs">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT TEASER */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-24 md:py-36">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <Reveal className="md:col-span-4">
            <div className="relative max-w-[320px]">
              <div className="overflow-hidden aspect-[4/5]">
                <img
                  src={portrait}
                  alt="Lizy Rodríguez"
                  className="portrait-frame w-full h-full object-cover"
                />
              </div>
            </div>
          </Reveal>
          <div className="md:col-span-7 md:col-start-6">
            <Reveal>
              <p className="overline mb-6">Sobre mí</p>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="font-display text-3xl md:text-4xl leading-tight tracking-tight mb-6">
                {home.aboutTeaser}
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <Link
                to="/sobre-mi"
                data-testid="home-about-link"
                className="inline-flex items-center gap-2 text-sm tracking-wide border-b border-[#1a1a1a] pb-1 hover:text-[#9C5A4B] hover:border-[#9C5A4B] transition-colors duration-300"
              >
                Conoce mi recorrido
                <ArrowRight size={16} />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* PULL QUOTE */}
      <section className="bg-[#efece5]" data-testid="home-pullquote">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-28 md:py-44">
          <Reveal>
            <p className="font-display text-3xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-center max-w-5xl mx-auto">
              “Escuchar no es buscar respuestas. Es hacer posible que aparezcan{" "}
              <span className="italic text-[#9C5A4B]">nuevas preguntas</span>.”
            </p>
          </Reveal>
        </div>
      </section>

      <EditorialMarquee />

      <BookCTA />
    </div>
  );
}
